import { useState, useCallback } from 'react';

// Type definitions
interface ColumnWidths {
    [fieldName: string]: number;
}

interface ColumnResizeParams {
    colDef: {
        field: string;
    };
    width: number;
}

interface UseColumnWidthsReturn {
    columnWidths: ColumnWidths;
    handleColumnResize: (params: ColumnResizeParams) => void;
}

export const getColumnWidthsFromStorage = (keyPrefix: string, defaultColumnWidths: ColumnWidths): ColumnWidths => {
    const storedWidths = localStorage.getItem(`${keyPrefix}ColumnWidths`);
    if (!storedWidths) return defaultColumnWidths;
    
    try {
        return JSON.parse(storedWidths);
    } catch (error) {
        // Return default widths if JSON is malformed
        return defaultColumnWidths;
    }
};

export const saveColumnWidthsToStorage = (keyPrefix: string, columnWidths: ColumnWidths): void => {
    localStorage.setItem(`${keyPrefix}ColumnWidths`, JSON.stringify(columnWidths));
};

/**
 * Custom hook to manage column widths with localStorage persistence
 * @param {string} storageKey - Unique key for localStorage (e.g., 'sbf', 'customer', 'product')
 * @param {ColumnWidths} defaultWidths - Object with field names as keys and default widths as values
 * @returns {UseColumnWidthsReturn} - Contains columnWidths state and handleColumnResize function
 */
export const useColumnWidths = (storageKey: string, defaultWidths: ColumnWidths): UseColumnWidthsReturn => {
    const [columnWidths, setColumnWidths] = useState<ColumnWidths>(
        getColumnWidthsFromStorage(storageKey, defaultWidths)
    );

    const handleColumnResize = useCallback((params: ColumnResizeParams) => {
        const updatedWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(updatedWidths);
        saveColumnWidthsToStorage(storageKey, updatedWidths);
    }, [columnWidths, storageKey]);

    return {
        columnWidths,
        handleColumnResize
    };
};

// Export types for use in other files
export type { ColumnWidths, ColumnResizeParams, UseColumnWidthsReturn }; 